import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { tg } from './tg';

export type ThemeName = 'light' | 'dark';
export type ThemePreference = ThemeName | 'system';

const STORAGE_KEY = 'polybot.theme';

interface ThemeContextValue {
  theme: ThemeName;
  preference: ThemePreference;
  /** True when the current preference is the default (no localStorage). */
  isDefault: boolean;
  setPreference: (p: ThemePreference) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): { value: ThemePreference; isDefault: boolean } {
  if (typeof window === 'undefined') return { value: 'system', isDefault: true };
  // Reading `localStorage` THROWS (SecurityError) — not just returns null —
  // in an iOS WKWebView with cookies/storage blocked (private browsing,
  // "Block All Cookies", tracking-prevention partitioning). This runs
  // during the root ThemeProvider's render, so an unguarded throw here
  // blanks the entire app before first paint. Fall back to the default.
  let v: string | null = null;
  try {
    v = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return { value: 'system', isDefault: true };
  }
  if (v === 'light' || v === 'dark' || v === 'system') {
    return { value: v, isDefault: false };
  }
  return { value: 'system', isDefault: true };
}

function detectSystemTheme(): ThemeName {
  if (typeof window === 'undefined') return 'dark';
  const tgScheme = tg.colorScheme;
  if (tgScheme === 'light' || tgScheme === 'dark') return tgScheme;
  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function resolveTheme(pref: ThemePreference): ThemeName {
  return pref === 'system' ? detectSystemTheme() : pref;
}

function applyToDom(theme: ThemeName) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const initial = readStoredPreference();
  const [preference, setPreferenceState] = useState<ThemePreference>(initial.value);
  const [isDefault, setIsDefault] = useState<boolean>(initial.isDefault);
  const [theme, setTheme] = useState<ThemeName>(() => resolveTheme(preference));

  useEffect(() => {
    applyToDom(theme);
  }, [theme]);

  // Re-resolve when preference changes or when 'system' source updates.
  useEffect(() => {
    setTheme(resolveTheme(preference));
    if (preference !== 'system') return;
    const mql = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mql) return;
    const onChange = () => setTheme(resolveTheme('system'));
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    setIsDefault(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // localStorage may be blocked (private browsing on iOS) — non-fatal,
      // we just lose the choice on next reload.
    }
  }, []);

  const toggle = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setPreference]);

  const value = useMemo(
    () => ({ theme, preference, isDefault, setPreference, toggle }),
    [theme, preference, isDefault, setPreference, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
